/**
 * Centralized date-handling utilities.
 * Eliminates the duplication of `new Date().toISOString()` across the codebase.
 */

const END_OF_DAY_HOURS = 23;
const END_OF_DAY_MINUTES = 59;
const END_OF_DAY_SECONDS = 59;
const END_OF_DAY_MS = 999;
const DATE_SLICE_LENGTH = 10;

/**
 * Gets the current date/time in ISO string format.
 * Replaces the repeated pattern: `new Date().toISOString()`
 */
export function nowISO(): string {
  return new Date().toISOString();
}

/**
 * Gets the end of the current day (23:59:59.999) in ISO string format.
 */
export function endOfTodayISO(): string {
  const d = new Date();
  d.setHours(END_OF_DAY_HOURS, END_OF_DAY_MINUTES, END_OF_DAY_SECONDS, END_OF_DAY_MS);
  return d.toISOString();
}

/**
 * Gets only the date part (YYYY-MM-DD) of the current date.
 *
 * NOTE: this is the UTC date (a stable key for dedupe/telemetry). For
 * CALENDAR dates the user perceives (schedules, attendance) use `localDateKey`.
 */
export function todayDateKey(): string {
  return new Date().toISOString().slice(0, DATE_SLICE_LENGTH);
}

/**
 * LOCAL date (YYYY-MM-DD) of an instant — the date the user sees on their
 * clock. `toISOString().slice(0,10)` returns the UTC date: in Venezuela
 * (UTC-4) from 20:00 onward it's already "tomorrow" in UTC (COV: the session
 * generator started on tomorrow and TODAY's attendance roll call couldn't
 * find its session), and in UTC+ time zones a local midnight falls on "yesterday".
 */
export function localDateKey(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
